import React, { useContext, useState } from "react"
import axios from "axios"
import { TextField, Select, InputLabel, Grid, FormControl, MenuItem, Dialog } from "@material-ui/core"
import Button from "components/common/Button"
import GridHeader from "components/common/GridHeader"
import useStyles from "assets/jss/material-global-styles"
import { commas, noCommas, useForm } from "helpers"
import { ReducerContext } from "App"
import { useConfig } from "hooks/useConfig"
import { isEthanolProduct, isEthanolTerminal } from "constants/productTypes"
import RailcarVisitVoid from "components/Inventory/RailcarVisitVoid"

const VOID_ROLES = ['manager', 'admin', 'developer']
const isReleased = status => (status ?? '').trim().toLowerCase() === 'not at facility'

const InventoryEdit = ({
  open,
  handleClose,
  getTermInv,
  sqlParams,
  specificInventoryData: {
    id: invID,
    inventory_id,
    owner,
    quantity,
    sand_type,
    storage_type,
    status,
    transloading_client,
    source_location,
    rr_bol_number,
    notes
  }
}) => {
  useStyles();
  const { state: { user: { id: dbUserId, permissions }, constsArrays: { sandType, clientName, storageType, inventoryStatus }, currentTerminal } } = useContext(ReducerContext)
  const { config } = useConfig()
  const [voidOpen, setVoidOpen] = useState(false)
  const canVoid = (permissions ?? []).some(permission => VOID_ROLES.some(role => role === permission)) && !isReleased(status)

  const isEthanol = isEthanolProduct(sand_type)
  const isMultiProduct = isEthanolTerminal(currentTerminal)
  const quantityLabel = isEthanol ? 'Quantity (gal)' : 'Quantity (lbs)'
  const inventoryIdLabel = isEthanol ? 'Tank Car ID' : 'Inventory ID'
  const productTypeLabel = isMultiProduct ? 'Product Type' : 'Sand Type'

  const init = {
    invID,
    inventory_id,
    owner,
    quantity,
    sand_type,
    storage_type,
    status,
    transloading_client,
    source_location,
    rr_bol_number: rr_bol_number ?? '',
    notes: notes ?? ''
  }
  const { setValue, values, isDirty } = useForm(init);

  const quantityRaw = noCommas(values["quantity"])
  const quantityInvalid = quantityRaw !== '' && !Number.isInteger(Number(quantityRaw))

  const handleForm = async () => {
    if (isDirty) {
      const { invID: id, quantity, ...rest } = values
      try {
        await axios.post(
          `${config.LOGITRAC_API_URL}api/upsert`,
          {
            array: [{
              ...rest,
              id,
              quantity: noCommas(quantity),
              updated_at: new Date().toISOString()
            }],
            dbTable: "Terminal_Inventory",
            dbEvent: "update",
            dbUnique: "id",
            dbUserId
          }
        )

        getTermInv({ ...sqlParams, page_number: 0, currentTerminal })
        handleClose()
      } catch (error) {
        console.log('error: ', error);
      }
    }

    handleClose()
  }

  const makeField = ({ id, label, onChange, value, error, helperText, optional, multiline }) => (
    <Grid item xs={12} sm={12} md={12} className="GridItem" key={id}>
      <TextField
        id={id}
        label={label}
        value={value ? value : values[id]}
        onChange={onChange ? onChange : e => setValue(e.target.id, e.target.value)}
        margin="normal"
        fullWidth
        required={!optional}
        multiline={multiline}
        error={error}
        helperText={helperText}
      />
    </Grid >
  )

  return (
    <div>
      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth={true}
        maxWidth="xl"
      >
        <Grid container className="GridContainer">
          <GridHeader h4="Edit Inventory Item" p="Please complete all details below.">
            <Grid container>
              {makeField({ id: "inventory_id", label: inventoryIdLabel })}
              {makeField({ id: "owner", label: "Owner" })}
              <Grid item xs={12} sm={12} md={12} style={{ marginTop: 10 }} className="GridItem">
                <FormControl fullWidth required>
                  <InputLabel>Storage Type</InputLabel>
                  <Select
                    name="storage_type"
                    onChange={e => setValue(e.target.name, e.target.value)}
                    value={values["storage_type"]}
                    fullWidth
                    required
                  >
                    {storageType.map((storageType, ind) => <MenuItem key={ind} value={storageType}>{storageType}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid >
              <Grid item xs={12} sm={12} md={12} style={{ marginTop: 20 }} className="GridItem">
                <FormControl fullWidth required>
                  <InputLabel>{productTypeLabel}</InputLabel>
                  <Select
                    name="sand_type"
                    onChange={e => setValue(e.target.name, e.target.value)}
                    value={values["sand_type"]}
                    fullWidth
                    required
                  >
                    {sandType.map((sandType, ind) => (<MenuItem key={ind} value={sandType}>{sandType}</MenuItem>))}
                  </Select>
                </FormControl>
              </Grid >
              <Grid item xs={12} sm={12} md={12} style={{ marginTop: 10 }} className="GridItem">
                <FormControl fullWidth required>
                  <InputLabel>Status</InputLabel>
                  <Select
                    name="status"
                    onChange={e => setValue(e.target.name, e.target.value)}
                    value={values["status"]}
                    fullWidth
                    required
                  >
                    {inventoryStatus.map((inventoryStatus, ind) => <MenuItem key={ind} value={inventoryStatus}>{inventoryStatus}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid >
              {makeField({
                id: "quantity",
                label: quantityLabel,
                value: commas(values["quantity"]),
                onChange: e => setValue(e.target.id, commas(e.target.value)),
                error: quantityInvalid,
                helperText: quantityInvalid ? `Must be a whole number (got '${quantityRaw}').` : ''
              })}
              {makeField({ id: "source_location", label: "Source Location" })}
              {makeField({ id: "rr_bol_number", label: "Railroad BOL # (optional)", optional: true })}
              {makeField({ id: "notes", label: "Notes (optional)", optional: true, multiline: true })}
              <Grid item xs={12} sm={12} md={12} style={{ marginTop: 10 }} className="GridItem">
                <FormControl fullWidth required>
                  <InputLabel>Transloading Client</InputLabel>
                  <Select
                    name="transloading_client"
                    onChange={e => setValue(e.target.name, e.target.value)}
                    value={values["transloading_client"]}
                    fullWidth
                    required
                  >
                    {clientName.map((clientName, ind) => <MenuItem key={ind} value={clientName}>{clientName}</MenuItem>)}
                  </Select>
                </FormControl>
              </Grid >
            </Grid>
            <Button
              color="primary"
              type="button"
              onClick={handleForm}
              disabled={(new Date(values["start_time"]) - new Date(values["end_time"])) > 0 || quantityInvalid}
            >
              Update Inventory Item
            </Button>
            {canVoid && (
              <Button color="danger" simple type="button" onClick={() => !isDirty && setVoidOpen(true)} disabled={isDirty}>
                {isDirty ? 'Save changes before voiding' : 'Void Visit (Not Really Here)'}
              </Button>
            )}
          </GridHeader>
        </Grid>
      </Dialog>
      {voidOpen && (
        <RailcarVisitVoid
          open={voidOpen}
          handleClose={() => setVoidOpen(false)}
          handleVoided={() => {
            setVoidOpen(false)
            getTermInv({ ...sqlParams, page_number: 0, currentTerminal })
            handleClose()
          }}
          inventoryRowId={invID}
          inventoryId={inventory_id}
          currentTerminal={currentTerminal}
        />
      )}
    </div >
  )
}

export default InventoryEdit
